import { CookieKey, coreNativeModule, type RNCoreNativeModule } from "../types/RNCoreNativeModule";

// DO NOT USE THIS FUNCTION DIRECTLY. USE provider specific getter instead.
function getCookies<K extends CookieKey>(
  cookieNames: readonly K[],
): Promise<Record<K, string | null> | Record<string, never>> {
  return coreNativeModule()?.getCookies?.(cookieNames) ?? Promise.resolve({});
}

// cookies shared across jobseeker pages (viewjob, homepage, SERP etc.)
const JOBSEEKER_READABLE_COOKIES = [
  CookieKey.referral,
  CookieKey.lastVisit,
  CookieKey.dsaCompliant,
  CookieKey.stealthGroups,
  CookieKey.recentSearch,
  // MAGUA app download promo frequency control cookies (shared across jobseeker pages)
  CookieKey.maguaPageVisitsCount,
  CookieKey.maguaPromoShownInSessionCount,
  CookieKey.maguaPromoShownInMonthOrWeekCount,
  CookieKey.maguaPromoClosedInSessionCount,
  CookieKey.maguaPromoClosedInMonthCount,
] as const;

const VIEWJOB_READABLE_COOKIES = [
  ...JOBSEEKER_READABLE_COOKIES,
  CookieKey.previousPageNumber,
  CookieKey.publisherNumber,
  CookieKey.clickTrackingLog,
  CookieKey.desktopClickTrackingLog,
  CookieKey.applyCountHistogram,
] as const;

// eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- RN-5783 baseline: pre-existing cast; migrate to zod/proper types (see docs/type-assertions.md)
const emptyViewjobCookies = Object.fromEntries(
  VIEWJOB_READABLE_COOKIES.map((cookie) => [cookie, null]),
) as Record<ViewJobReadableCookies, string | null>;

export type ViewJobReadableCookies = (typeof VIEWJOB_READABLE_COOKIES)[number];

export async function getCookiesForViewJob(
  api: Required<RNCoreNativeModule>,
): Promise<Record<ViewJobReadableCookies, string | null>> {
  const cookies = await api.getCookies(VIEWJOB_READABLE_COOKIES);
  return cookies ?? emptyViewjobCookies;
}

export const HOMEPAGE_READABLE_COOKIES = [
  ...JOBSEEKER_READABLE_COOKIES,
  CookieKey.recentSearch,
] as const;

export type HomepageReableCookies = (typeof HOMEPAGE_READABLE_COOKIES)[number];

export async function getCookiesForHomepage(): Promise<
  Record<HomepageReableCookies, string | null>
> {
  const cookies = await getCookies(HOMEPAGE_READABLE_COOKIES);
  return cookies;
}

const ACE_READABLE_COOKIES = [
  CookieKey.csW2aPageVisitsCount,
  CookieKey.csW2aPromoShownInSessionCount,
  CookieKey.csW2aPromoShownInMonthOrWeekCount,
] as const;

export type AceReadableCookies = (typeof ACE_READABLE_COOKIES)[number];

// eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- RN-5783: empty cookie map for Ace web
const emptyAceCookies = Object.fromEntries(
  ACE_READABLE_COOKIES.map((cookie) => [cookie, null]),
) as Record<AceReadableCookies, string | null>;

export async function getCookiesForAce(): Promise<Record<AceReadableCookies, string | null>> {
  const cookies = await getCookies(ACE_READABLE_COOKIES);
  if (Object.keys(cookies).length === 0) {
    return emptyAceCookies;
  }
  // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- RN-5783: getCookies union includes empty map
  return cookies as Record<AceReadableCookies, string | null>;
}
